'use client'

import MuiTimeline from '@mui/lab/Timeline'
import MuiTimelineConnector from '@mui/lab/TimelineConnector'
import MuiTimelineContent from '@mui/lab/TimelineContent'
import MuiTimelineItem from '@mui/lab/TimelineItem'
import MuiTimelineOppositeContent from '@mui/lab/TimelineOppositeContent'
import MuiTimelineSeparator from '@mui/lab/TimelineSeparator'
import { styled } from '@mui/material'
import Box from '@mui/material/Box'
import type { JSX } from 'react'
import { ReactElement, useState } from 'react'
import ButtonLink from '../Button/ButtonLink'
import { IconType } from '../Icon'
import { ExpandMoreIcon } from '../Icon/catalog'

export type TimelineItem = {
    id: string
    content: JSX.Element
    Icon: IconType
    className?: string
    key?: string
}

export type TimelineProps = { items: TimelineItem[] } & (
    | {
          /**
           * Number of non-collapsible items. Omit to show all.
           */
          visibleItems?: never
          /**
           * Number of items to display at the bottom. These are always visible.
           */
          tail?: never
      }
    | {
          visibleItems: number
          tail?: TimelineItem[]
      }
)

const StyledButtonLink = styled(ButtonLink)(() => ({
    '&:hover': {
        textDecoration: 'underline',
    },
}))

function Timeline(props: TimelineProps): ReactElement {
    const [isExpanded, setIsExpanded] = useState(false)

    const tail = props.tail != undefined ? [...props.tail] : []
    const tailLength = tail.length

    const visibleItems = props.visibleItems != undefined ? props.visibleItems : 0

    const items = visibleItems > 0 ? props.items.slice(0, visibleItems) : [...props.items]
    const itemsLength = items.length

    const collapsedItems = visibleItems > 0 ? props.items.slice(visibleItems, props.items.length) : []
    const collapsedItemsLength = collapsedItems.length

    const isCollapsible = collapsedItemsLength > 0

    return (
        <MuiTimeline>
            {items.map((item, index) => {
                return (
                    <TimelineEntry
                        {...item}
                        key={item.key ?? item.id}
                        hasConnector={itemsLength - 1 !== index || isCollapsible || tailLength > 0}
                        data-testid={`${item.key}-timeline-entry`}
                    />
                )
            })}
            {isCollapsible && !isExpanded ? (
                <TimelineEntry
                    id="show-more-button"
                    Icon={ExpandMoreIcon}
                    hasConnector={tailLength > 0}
                    content={
                        <Box sx={{ mt: 0.5 }}>
                            <StyledButtonLink onClick={() => setIsExpanded(true)}>Show more</StyledButtonLink>
                        </Box>
                    }
                />
            ) : null}
            {isExpanded
                ? collapsedItems.map((item, index) => (
                      <TimelineEntry
                          {...item}
                          key={item.key ?? item.id}
                          hasConnector={collapsedItemsLength - 1 !== index || tailLength > 0}
                          data-testid={`${item.key}-timeline-entry`}
                      />
                  ))
                : null}
            {tail.map((item, index) => (
                <TimelineEntry {...item} key={item.key ?? item.id} hasConnector={tailLength - 1 !== index} />
            ))}
        </MuiTimeline>
    )
}

type TimelineEntryProps = TimelineItem & { hasConnector: boolean }

function TimelineEntry({ Icon, content, hasConnector, className }: TimelineEntryProps) {
    return (
        <MuiTimelineItem className={className}>
            <MuiTimelineOppositeContent />
            <MuiTimelineSeparator>
                <Icon color="divider" />
                {hasConnector ? <MuiTimelineConnector /> : null}
            </MuiTimelineSeparator>
            <MuiTimelineContent variant="body2">{content}</MuiTimelineContent>
        </MuiTimelineItem>
    )
}

export default Timeline
